import { clientLog } from './clientLog'

export class ApiError extends Error {
  constructor(public status: number, public code: string) {
    super(`${status} ${code}`)
    this.name = 'ApiError'
  }
}

// Endpoints we should NEVER report from clientLog — otherwise a failing log
// post creates an infinite loop, and noise that's irrelevant to triage.
const LOG_BLACKLIST = ['/api/log/']

function shouldReport(url: string): boolean {
  return !LOG_BLACKLIST.some((p) => url.startsWith(p))
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content-type when we're actually sending a body.
  // Otherwise Fastify rejects the empty body with FST_ERR_CTP_EMPTY_JSON_BODY.
  const headers: Record<string, string> = { ...((init?.headers ?? {}) as Record<string, string>) }
  if (init?.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }
  const method = init?.method ?? 'GET'
  let res: Response
  try {
    res = await fetch(url, {
      credentials: 'include',
      headers,
      ...init,
    })
  } catch (err) {
    // Network-layer failure: fetch threw (TypeError) before getting a response.
    // Caller's catch will see this as a non-ApiError; we still want server logs.
    if (shouldReport(url)) {
      clientLog.error({
        message: `fetch_failed ${method} ${url}`,
        url,
        code: 'fetch_failed',
        context: { method, reason: err instanceof Error ? err.message : String(err) },
      })
    }
    throw err
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'unknown_error' }))
    const code = (body as { error?: string }).error ?? 'unknown_error'
    if (shouldReport(url)) {
      clientLog.error({
        message: `${res.status} ${code} ${method} ${url}`,
        url,
        status: res.status,
        code,
        // Request ID, if Fastify echoed one — handy for cross-referencing
        // with the server log line.
        request_id: res.headers.get('x-request-id') ?? undefined,
        context: { method },
      })
    }
    throw new ApiError(res.status, code)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export interface PublicWorkshopListEntry {
  slug: string
  title: string
  description: string
  auth_mode: 'password' | 'magic_link'
}

/** v0.13 — global catalog entry includes owner attribution. */
export interface GlobalCatalogEntry extends PublicWorkshopListEntry {
  owner_admin_slug: string | null
  owner_display_name: string | null
}

/** v0.13 — per-admin catalog entry includes publish_state. */
export interface AdminCatalogEntry extends PublicWorkshopListEntry {
  publish_state: 'hidden' | 'private' | 'public'
}

/** v0.13 — admin profile surfaced on /by/{slug}. */
export interface AdminCatalogProfile {
  admin_slug: string
  display_name: string
  /** v0.13 Phase E — resolved admin brand. Falls through to platform. */
  tagline?: string | null
  accent_palette?: string | null
  /** v1.0 — resolved logo URL for this admin (per-admin override or platform). */
  logo_url?: string | null
}

export type WorkshopTimeState = 'untimed' | 'armed' | 'running' | 'expired'

export interface WorkshopInfo {
  id: string
  slug: string
  title: string
  description: string
  auth_mode: 'password' | 'magic_link'
  status: 'draft' | 'active' | 'closed'
  // Timed-mode fields. All clients should treat the server's `time_state`
  // as authoritative and use `server_now` to compute their local clock
  // offset for smooth countdown rendering.
  timed_mode: boolean
  duration_minutes: number | null
  started_at: string | null
  time_state: WorkshopTimeState
  server_now: string
  // Phase B — display mode. Server always sends concrete defaults so the
  // client doesn't have to fall back.
  mode: 'standard' | 'ctf'
  ctf_view: 'normal' | 'jeopardy'
  /** Enter inside text-match answer inputs submits when true (default). */
  enter_submits_text_answers: boolean
  /** First-Blood bonus points (informational on this surface; server enforces). */
  first_blood_bonus: number
  /** Notch icon shown next to first-blood holders on the leaderboard. */
  first_blood_icon: string
  /** v0.12 — Hidden from the public landing page directory (slug-only access). */
  unlisted: boolean
  /**
   * v0.13 multi-tenant fields. `_access` is computed server-side per request:
   * 'owner' = full edit + delete; 'scope' = operate-only on a global workshop
   * you don't own.
   */
  publish_state?: 'hidden' | 'private' | 'public'
  owner_id?: string
  admin_slug?: string
  _access?: 'owner' | 'scope'
}

export interface PublicUser {
  id: string
  email: string
  display_name: string
  role: 'participant' | 'admin'
  registered_at: string
  theme?: 'light' | 'dark'
}

export const api = {
  // ── Public discovery ─────────────────────────────────────────
  publicWorkshops: () =>
    request<{ workshops: PublicWorkshopListEntry[] }>('/api/workshops'),

  /** v0.13 — global catalog with owner attribution. */
  catalogGlobal: () =>
    request<{ workshops: GlobalCatalogEntry[] }>('/api/catalog/global'),

  /** v0.13 — per-admin catalog with publish_state per workshop. */
  catalogByAdmin: (adminSlug: string) =>
    request<{ admin: AdminCatalogProfile; workshops: AdminCatalogEntry[] }>(
      `/api/catalog/by/${encodeURIComponent(adminSlug)}`,
    ),

  // v0.13 — admin signup + invite flow.
  adminSubmitSignupRequest: (body: {
    email: string
    display_name: string
    requested_slug?: string
    reason?: string
  }) =>
    request<{ request: AdminSignupRequest; already_pending?: boolean }>(
      '/api/admin/auth/signup-request',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  adminListSignupRequests: () =>
    request<{ requests: AdminSignupRequest[] }>('/api/admin/auth/signup-requests'),

  adminApproveSignupRequest: (id: string) =>
    request<{ admin: AdminAccount; email_sent: boolean; invite_url?: string }>(
      `/api/admin/auth/signup-requests/${id}/approve`,
      { method: 'POST' },
    ),

  adminRejectSignupRequest: (id: string, reason?: string) =>
    request<{ request: AdminSignupRequest }>(
      `/api/admin/auth/signup-requests/${id}/reject`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  adminDirectInvite: (body: {
    email: string
    display_name: string
    preferred_slug?: string
  }) =>
    request<{ admin: AdminAccount; email_sent: boolean; invite_url?: string }>(
      '/api/admin/auth/admins/invite',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  adminFetchInvite: (token: string) =>
    request<{ admin: { email: string; display_name: string } }>(
      `/api/admin/auth/invite/${encodeURIComponent(token)}`,
    ),

  adminAcceptInvite: (token: string, password: string) =>
    request<{ admin: { id: string; email: string } }>(
      `/api/admin/auth/invite/${encodeURIComponent(token)}/accept`,
      { method: 'POST', body: JSON.stringify({ password }) },
    ),

  /** v0.13 — admin role/status mutations (superadmin-only routes; others 403). */
  adminSetRole: (id: string, role: 'admin' | 'superadmin') =>
    request<{ admin: unknown }>(`/api/admin/auth/admins/${id}/role`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    }),

  adminSetStatus: (id: string, status: 'active' | 'disabled') =>
    request<{ admin: unknown }>(`/api/admin/auth/admins/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  workshopInfo: (slug: string) =>
    request<WorkshopInfo>(`/api/workshops/${slug}/info`),

  participantMe: (slug: string) =>
    request<{ participant: PublicUser | null }>(`/api/workshops/${slug}/auth/me`),

  participantLogin: (slug: string, body: { email: string; password: string }) =>
    request<{ user: PublicUser }>(`/api/workshops/${slug}/auth/login`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  participantRegister: (
    slug: string,
    body: { email: string; display_name: string; password?: string },
  ) =>
    request<{ user?: PublicUser; sent?: boolean }>(
      `/api/workshops/${slug}/auth/register`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  participantRequestLink: (slug: string, body: { email: string }) =>
    request<{ sent: boolean }>(`/api/workshops/${slug}/auth/request-link`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  participantLogout: (slug: string) =>
    request<{ ok: boolean }>(`/api/workshops/${slug}/auth/logout`, { method: 'POST' }),

  participantForgotPassword: (slug: string, body: { email: string }) =>
    request<{ sent: boolean }>(`/api/workshops/${slug}/auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  participantVerifyResetToken: (slug: string, token: string) =>
    request<{ valid: true; email: string }>(
      `/api/workshops/${slug}/auth/reset/${encodeURIComponent(token)}`,
    ),

  participantResetPassword: (slug: string, token: string, body: { password: string }) =>
    request<{ user: PublicUser }>(`/api/workshops/${slug}/auth/reset/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  participantSetTheme: (slug: string, theme: 'light' | 'dark') =>
    request<{ theme: 'light' | 'dark' }>(`/api/workshops/${slug}/auth/me/theme`, {
      method: 'PUT',
      body: JSON.stringify({ theme }),
    }),

  adminLogin: (body: { email: string; password: string }) =>
    request<{ admin: { id: string; email: string } }>(`/api/admin/auth/login`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  adminMe: () =>
    request<{
      admin: {
        sub: string
        email: string
        role: 'admin' | 'superadmin'
        admin_slug?: string
        // Server augments the JWT claims with the admin's stored preferences.
        theme?: 'light' | 'dark'
        /** v0.13 Phase E — resolved per-admin brand (override → platform). */
        display_name?: string | null
        tagline?: string | null
        accent_palette?: string | null
      } | null
    }>(`/api/admin/auth/me`),

  // v0.13 Phase E — per-admin branding profile.
  // v1.0 — extended with logo_url + resolved.logo_url.
  adminGetProfile: () =>
    request<{
      admin_slug: string | null
      email: string
      display_name: string | null
      tagline: string | null
      accent_palette: string | null
      logo_url: string | null
      resolved: {
        display_name: string
        tagline: string | null
        accent_palette: string | null
        logo_url: string | null
      }
    }>(`/api/admin/auth/me/profile`),

  adminUpdateProfile: (body: {
    display_name?: string | null
    tagline?: string | null
    accent_palette?: string | null
  }) =>
    request<{
      ok: boolean
      resolved: {
        display_name: string
        tagline: string | null
        accent_palette: string | null
        logo_url: string | null
      }
    }>(`/api/admin/auth/me/profile`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** v1.0 — upload a per-admin logo. Replaces any existing one. */
  adminUploadProfileLogo: async (file: File) => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch('/api/admin/auth/me/profile/logo', {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'upload_failed' }))
      throw new ApiError(res.status, (body as { error?: string }).error ?? 'upload_failed')
    }
    return (await res.json()) as {
      ok: true
      logo_url: string
      resolved: {
        display_name: string
        tagline: string | null
        accent_palette: string | null
        logo_url: string | null
      }
    }
  },

  adminDeleteProfileLogo: () =>
    request<{
      ok: true
      logo_url: null
      resolved: {
        display_name: string
        tagline: string | null
        accent_palette: string | null
        logo_url: string | null
      }
    }>(`/api/admin/auth/me/profile/logo`, { method: 'DELETE' }),

  adminSetTheme: (theme: 'light' | 'dark') =>
    request<{ theme: 'light' | 'dark' }>(`/api/admin/auth/me/theme`, {
      method: 'PUT',
      body: JSON.stringify({ theme }),
    }),

  adminLogout: () => request<{ ok: boolean }>(`/api/admin/auth/logout`, { method: 'POST' }),

  // ── Admin workshop CRUD ──────────────────────────────────────
  adminListWorkshops: () =>
    request<{ workshops: AdminWorkshop[]; pinned_ids: string[] }>('/api/admin/workshops'),

  adminPinWorkshop: (workshopId: string) =>
    request<{ pinned_ids: string[] }>(`/api/admin/workshops/${workshopId}/pin`, {
      method: 'POST',
    }),

  adminUnpinWorkshop: (workshopId: string) =>
    request<{ pinned_ids: string[] }>(`/api/admin/workshops/${workshopId}/unpin`, {
      method: 'POST',
    }),

  adminSetWorkshopPinOrder: (order: string[]) =>
    request<{ pinned_ids: string[] }>(`/api/admin/workshops/pin-order`, {
      method: 'POST',
      body: JSON.stringify({ order }),
    }),

  adminCreateWorkshop: (body: { title: string; slug?: string; description?: string; auth_mode?: 'password' | 'magic_link' }) =>
    request<{ workshop: AdminWorkshop }>('/api/admin/workshops', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  adminGetWorkshop: (id: string) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}`),

  adminUpdateWorkshop: (
    id: string,
    body: {
      title?: string
      description?: string
      auth_mode?: 'password' | 'magic_link'
      mode?: 'standard' | 'ctf'
      ctf_view?: 'normal' | 'jeopardy'
      enter_submits_text_answers?: boolean
      first_blood_bonus?: number
      first_blood_icon?: string
      unlisted?: boolean
      publish_state?: 'hidden' | 'private' | 'public'
    },
  ) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  adminDeleteWorkshop: (id: string) =>
    request<void>(`/api/admin/workshops/${id}`, { method: 'DELETE' }),

  adminActivateWorkshop: (id: string) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}/activate`, { method: 'POST' }),

  adminCloseWorkshop: (id: string) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}/close`, { method: 'POST' }),

  adminConfigureTiming: (id: string, enabled: boolean, duration_minutes: number | null) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}/timing`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, duration_minutes }),
    }),

  adminStartTimer: (id: string) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}/timer/start`, { method: 'POST' }),

  adminStopTimer: (id: string) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}/timer/stop`, { method: 'POST' }),

  adminResetTimer: (id: string) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/workshops/${id}/timer/reset`, { method: 'POST' }),

  // ── Admin chapter CRUD ───────────────────────────────────────
  adminListChapters: (workshopId: string) =>
    request<{ chapters: ChapterMeta[] }>(`/api/admin/workshops/${workshopId}/chapters`),

  adminGetChapter: (workshopId: string, chapterId: string) =>
    request<{ chapter: ChapterFull }>(`/api/admin/workshops/${workshopId}/chapters/${chapterId}`),

  adminCreateChapter: (workshopId: string, body: { title: string; slug?: string; published?: boolean }) =>
    request<{ chapter: ChapterFull }>(`/api/admin/workshops/${workshopId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  adminUpdateChapter: (
    workshopId: string,
    chapterId: string,
    body: { title?: string; slug?: string; published?: boolean; title_page?: boolean; content?: string },
  ) =>
    request<{ chapter: ChapterFull }>(`/api/admin/workshops/${workshopId}/chapters/${chapterId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  adminDeleteChapter: (workshopId: string, chapterId: string) =>
    request<void>(`/api/admin/workshops/${workshopId}/chapters/${chapterId}`, {
      method: 'DELETE',
    }),

  adminReorderChapters: (workshopId: string, order: string[]) =>
    request<{ chapters: ChapterMeta[] }>(`/api/admin/workshops/${workshopId}/chapters/reorder`, {
      method: 'POST',
      body: JSON.stringify({ order }),
    }),

  // ── Admin challenge CRUD ─────────────────────────────────────
  adminListChallenges: (workshopId: string) =>
    request<{ challenges: AdminChallenge[] }>(`/api/admin/workshops/${workshopId}/challenges`),

  adminCreateChallenge: (workshopId: string, body: AdminChallengePayload) =>
    request<{ challenge: AdminChallenge }>(`/api/admin/workshops/${workshopId}/challenges`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  adminUpdateChallenge: (workshopId: string, cid: string, body: AdminChallengePayload) =>
    request<{ challenge: AdminChallenge }>(`/api/admin/workshops/${workshopId}/challenges/${cid}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  adminToggleChallengePublish: (workshopId: string, cid: string) =>
    request<{ challenge: AdminChallenge }>(`/api/admin/workshops/${workshopId}/challenges/${cid}/publish`, {
      method: 'POST',
    }),

  adminDeleteChallenge: (workshopId: string, cid: string) =>
    request<void>(`/api/admin/workshops/${workshopId}/challenges/${cid}`, { method: 'DELETE' }),

  /** Download URL for every challenge as challenges.md. */
  adminChallengesFileUrl: (workshopId: string) => `/api/admin/workshops/${workshopId}/challenges/_file`,

  /** Preview (apply=false) or apply a challenges.md upload. */
  adminChallengesFileImport: (
    workshopId: string,
    markdown: string,
    opts: { apply?: boolean; confirm_live?: boolean } = {},
  ) =>
    request<{ plan: ChallengesFilePlan; challenges?: AdminChallenge[] }>(
      `/api/admin/workshops/${workshopId}/challenges/_file`,
      { method: 'POST', body: JSON.stringify({ markdown, ...opts }) },
    ),

  adminReorderChallenges: (workshopId: string, orderedIds: string[]) =>
    request<{ challenges: AdminChallenge[] }>(
      `/api/admin/workshops/${workshopId}/challenges/reorder`,
      { method: 'POST', body: JSON.stringify({ ordered_ids: orderedIds }) },
    ),

  // ── Admin per-challenge file attachments ─────────────────────
  /**
   * Upload one attachment file to a challenge. Goes through a FormData POST
   * so we keep the same credentials-include cookie auth as the rest of the
   * admin surface; throws an ApiError with the server `error` code on 4xx.
   */
  adminUploadChallengeFile: async (workshopId: string, cid: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(
      `/api/admin/workshops/${workshopId}/challenges/${cid}/files`,
      { method: 'POST', credentials: 'include', body: form },
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'upload_failed' }))
      throw new ApiError(res.status, (body as { error?: string }).error ?? 'upload_failed')
    }
    return (await res.json()) as { file: ChallengeFile }
  },

  adminDeleteChallengeFile: (workshopId: string, cid: string, fid: string) =>
    request<void>(
      `/api/admin/workshops/${workshopId}/challenges/${cid}/files/${fid}`,
      { method: 'DELETE' },
    ),

  /** Participant-side download URL for a challenge attachment. */
  participantChallengeFileUrl: (slug: string, cid: string, fid: string) =>
    `/api/workshops/${slug}/challenges/${cid}/files/${fid}`,

  adminLeaderboard: (workshopId: string) =>
    request<{ entries: LeaderboardEntry[]; first_blood_icon: string }>(
      `/api/admin/workshops/${workshopId}/challenges/_leaderboard`,
    ),

  adminEventFeed: (workshopId: string, since: number) =>
    request<{ events: FeedEvent[] }>(
      `/api/admin/workshops/${workshopId}/challenges/_event-feed?since=${since}`,
    ),

  // ── Admin platform settings ──────────────────────────────────
  adminGetPlatformSettings: () =>
    request<PlatformSettingsResponse>('/api/admin/platform/settings'),

  adminPutTls: (body: TlsUpdateBody) =>
    request<{ tls: TlsMasked }>('/api/admin/platform/tls', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  adminApplyTls: () =>
    request<{ ok: boolean; output: string }>('/api/admin/platform/tls/apply', { method: 'POST' }),

  adminTlsStatus: () =>
    request<TlsStatus>('/api/admin/platform/tls/status'),

  adminImportCert: (body: { certificate: string; chain?: string; private_key: string; passphrase?: string }) =>
    request<{ installed: InstalledCert & { hostname_checked: boolean }; tls: TlsMasked; apply: { ok: boolean; output: string } }>(
      '/api/admin/platform/tls/import',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  adminGetCsr: () => request<{ pending: PendingCsr | null }>('/api/admin/platform/tls/csr'),

  adminGenerateCsr: (body: CsrRequestBody) =>
    request<{ pending: PendingCsr }>('/api/admin/platform/tls/csr', { method: 'POST', body: JSON.stringify(body) }),

  adminDiscardCsr: () => request<{ pending: null }>('/api/admin/platform/tls/csr', { method: 'DELETE' }),

  adminInstallIssuedCert: (certificate: string) =>
    request<{ installed: InstalledCert; tls: TlsMasked; apply: { ok: boolean; output: string } }>(
      '/api/admin/platform/tls/csr/certificate',
      { method: 'POST', body: JSON.stringify({ certificate }) },
    ),

  adminPutBranding: (body: BrandingUpdateBody) =>
    request<{ branding: BrandingPublic }>('/api/admin/platform/branding', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  adminUploadBranding: async (slot: 'logo' | 'banner' | 'lobby', file: File) => {
    const form = new FormData()
    form.append('slot', slot)
    form.append('file', file)
    const res = await fetch('/api/admin/platform/branding/upload', {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'upload_failed' }))
      throw new ApiError(res.status, (body as { error?: string }).error ?? 'upload_failed')
    }
    return (await res.json()) as {
      branding: BrandingPublic
      slot: 'logo' | 'banner' | 'lobby'
      url: string
    }
  },

  // ── Public Facing Lobby ──────────────────────────────────────
  /**
   * Configure (set/rotate the code, toggle the gate) the lobby. Either field
   * is optional; setting `code` rotates and invalidates outstanding cookies.
   */
  adminPutLobby: (body: { enabled?: boolean; code?: string | null }) =>
    request<{ lobby: LobbyState }>('/api/admin/platform/lobby', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** Public gate status — used by the SPA router to decide on /lobby redirect. */
  gateStatus: () =>
    request<{ enabled: boolean; lobby_image_url: string | null; verified: boolean }>(
      '/api/gate/status',
    ),

  /** Trade an entry code for the `workshop_lobby` cookie. Rate-limited server-side. */
  gateVerify: (code: string) =>
    request<{ ok: boolean }>('/api/gate/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** Public license-request form submission. Honeypot + rate-limited. */
  submitLicenseRequest: (body: LicenseRequestBody) =>
    request<{ ok: true }>('/api/license/request', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── License ──────────────────────────────────────────────────
  adminGetLicense: () =>
    request<LicenseSnapshot>('/api/admin/platform/license'),

  adminInstallLicense: (token: string) =>
    request<LicenseSnapshot>('/api/admin/platform/license', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  adminUninstallLicense: () =>
    request<LicenseSnapshot>('/api/admin/platform/license', { method: 'DELETE' }),

  adminGetUpdates: () => request<UpdatesSnapshot>('/api/admin/platform/updates'),

  adminCheckUpdates: () =>
    request<UpdatesSnapshot>('/api/admin/platform/updates/check', { method: 'POST' }),

  adminSetUpdateSettings: (autoCheck: boolean) =>
    request<UpdatesSnapshot>('/api/admin/platform/updates/settings', {
      method: 'PUT',
      body: JSON.stringify({ auto_check: autoCheck }),
    }),

  adminApplyUpdate: (version: string, confirm: string) =>
    request<{ requested: string }>('/api/admin/platform/updates/apply', {
      method: 'POST',
      body: JSON.stringify({ version, confirm }),
    }),

  adminSetLicenseVisibility: (show: boolean) =>
    request<LicenseSnapshot>('/api/admin/platform/license/visibility', {
      method: 'PUT',
      body: JSON.stringify({ show_licensed_org: show }),
    }),

  adminPutEmail: (body: EmailUpdateBody) =>
    request<{ email: EmailMasked }>('/api/admin/platform/email', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  adminSendTestEmail: (to?: string) =>
    request<{ sent: boolean; to: string }>('/api/admin/platform/email/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    }),

  adminPutDefaultAuthMode: (mode: 'password' | 'magic_link' | null) =>
    request<{ default_auth_mode: 'password' | 'magic_link' }>(
      '/api/admin/platform/default-auth-mode',
      { method: 'PUT', body: JSON.stringify({ default_auth_mode: mode }) },
    ),

  // ── Admin account ────────────────────────────────────────────
  adminChangePassword: (current: string, next: string) =>
    request<{ ok: boolean }>('/api/admin/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: current, new_password: next }),
    }),

  adminListAdmins: () =>
    request<{ admins: AdminAccount[]; orphaned_workshops?: number }>('/api/admin/auth/admins'),

  adminAddAdmin: (email: string, password: string, displayName?: string) =>
    request<{ admin: AdminAccount }>('/api/admin/auth/admins', {
      method: 'POST',
      body: JSON.stringify({ email, password, display_name: displayName }),
    }),

  /** Delete an admin. If they own workshops, say whether to delete or migrate them. */
  adminRemoveAdmin: (id: string, opts?: { workshops: 'delete' | 'migrate'; target_id?: string }) =>
    request<void>(`/api/admin/auth/admins/${id}`, {
      method: 'DELETE',
      ...(opts ? { body: JSON.stringify(opts) } : {}),
    }),

  adminAdminOwned: (id: string) => request<AdminOwned>(`/api/admin/auth/admins/${id}/owned`),

  adminAdminBundlesUrl: (id: string) => `/api/admin/auth/admins/${id}/bundles`,

  // ── Admin users ──────────────────────────────────────────────
  adminListUsers: (workshopId: string) =>
    request<{ users: AdminParticipant[] }>(`/api/admin/workshops/${workshopId}/users`),

  adminDeleteUser: (workshopId: string, uid: string) =>
    request<void>(`/api/admin/workshops/${workshopId}/users/${uid}`, { method: 'DELETE' }),

  adminResetUserPassword: (workshopId: string, uid: string) =>
    request<{ temporary_password: string }>(`/api/admin/workshops/${workshopId}/users/${uid}/reset-password`, {
      method: 'POST',
    }),

  // ── Admin audit log ──────────────────────────────────────────
  adminListAudit: (
    workshopId: string,
    filters: Partial<{ user_id: string; challenge_id: string; chapter_id: string; event_type: string; limit: number; offset: number }> = {},
  ) => {
    const qs = new URLSearchParams()
    if (filters.user_id) qs.set('user_id', filters.user_id)
    if (filters.challenge_id) qs.set('challenge_id', filters.challenge_id)
    if (filters.chapter_id) qs.set('chapter_id', filters.chapter_id)
    if (filters.event_type) qs.set('event_type', filters.event_type)
    if (filters.limit) qs.set('limit', String(filters.limit))
    if (filters.offset) qs.set('offset', String(filters.offset))
    const s = qs.toString()
    return request<{ events: AuditEvent[]; total_match: number }>(
      `/api/admin/workshops/${workshopId}/audit${s ? '?' + s : ''}`,
    )
  },

  adminRevertAudit: (workshopId: string, eventId: string) =>
    request<
      | { reverted_event_id: string; revert_event_id: string; points_refunded: number }
      | { already_reverted: true; reverted_by: string }
    >(`/api/admin/workshops/${workshopId}/audit/${eventId}/revert`, { method: 'POST' }),

  // ── Platform admin audit ─────────────────────────────────────
  adminListPlatformAudit: (
    filters: Partial<{
      actor_id: string
      action: string
      action_prefix: string
      target_type: string
      target_id: string
      outcome: 'success' | 'failure'
      before: string
      after: string
      limit: number
      offset: number
    }> = {},
  ) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    }
    const s = qs.toString()
    return request<{ events: AdminAuditEvent[]; total_match: number }>(
      `/api/admin/audit${s ? '?' + s : ''}`,
    )
  },

  adminListPlatformAuditMeta: () =>
    request<{ actors: AdminAuditActor[]; actions: string[] }>(`/api/admin/audit/meta`),

  adminExportPlatformAuditUrl: (filters: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    }
    const s = qs.toString()
    return `/api/admin/audit/export${s ? '?' + s : ''}`
  },

  adminPurgePlatformAudit: (beforeIso: string) =>
    request<{ purged: number; cutoff: string }>(`/api/admin/audit`, {
      method: 'DELETE',
      body: JSON.stringify({ before: beforeIso }),
    }),

  // ── Platform backup / restore ────────────────────────────────
  adminBackupUrl: (includeEnv: boolean) =>
    `/api/admin/platform/backup?include_env=${includeEnv ? 'true' : 'false'}`,

  adminPreviewBackup: async (file: File): Promise<{ manifest: BackupManifest; warnings: string[] }> => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch('/api/admin/platform/backup/preview', {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'preview_failed' }))
      throw new ApiError(res.status, (body as { error?: string }).error ?? 'preview_failed')
    }
    return (await res.json()) as { manifest: BackupManifest; warnings: string[] }
  },

  adminRestoreBackup: async (file: File): Promise<RestoreResult> => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch('/api/admin/platform/restore', {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) {
      const body = (await res.json().catch(() => ({ error: 'restore_failed' }))) as { error?: string; message?: string }
      throw new ApiError(res.status, body.error ?? 'restore_failed')
    }
    return (await res.json()) as RestoreResult
  },

  // ── Workshop protect ─────────────────────────────────────────
  adminProtectWorkshop: (workshopId: string) =>
    request<{ workshop: AdminWorkshop; already_protected?: true }>(
      `/api/admin/workshops/${workshopId}/protect`,
      { method: 'POST' },
    ),

  adminUnprotectWorkshop: (workshopId: string) =>
    request<{ workshop: AdminWorkshop; already_unprotected?: true }>(
      `/api/admin/workshops/${workshopId}/unprotect`,
      { method: 'POST' },
    ),

  adminUnlockWorkshop: (workshopId: string) =>
    request<{ unlocked: boolean; reason?: string; ttl_seconds?: number }>(
      `/api/admin/workshops/${workshopId}/unlock`,
      { method: 'POST' },
    ),

  // ── Admin workshop lifecycle ─────────────────────────────────
  adminResetWorkshop: (workshopId: string) =>
    request<{ workshop: AdminWorkshop; archived_export: string | null }>(
      `/api/admin/workshops/${workshopId}/reset`,
      { method: 'POST' },
    ),

  adminExportResultsUrl: (workshopId: string) =>
    `/api/admin/workshops/${workshopId}/export/results`,

  adminExportPdfUrl: (workshopId: string) =>
    `/api/admin/workshops/${workshopId}/export/pdf`,

  // ── Workshop bundle (content portability) ────────────────────
  adminExportBundleUrl: (workshopId: string, includeArchive: boolean) =>
    `/api/admin/workshops/${workshopId}/export/bundle?include_archive=${includeArchive}`,

  adminImportWorkshop: async (
    file: File,
    slug: string,
    title: string,
  ): Promise<{ workshop: AdminWorkshop }> => {
    const form = new FormData()
    // Fields must precede the file — the server stops parsing fields at the
    // first file part.
    form.append('slug', slug)
    form.append('title', title)
    form.append('file', file)
    const res = await fetch('/api/admin/workshops/import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'import_failed' }))
      throw new ApiError(res.status, (body as { error?: string }).error ?? 'import_failed')
    }
    return (await res.json()) as { workshop: AdminWorkshop }
  },

  // New workshop from a Google Docs export pair (Markdown + PDF). Files go up
  // in chunks so no single request trips a proxy body limit.
  adminDocImportStart: (md: File, pdf: File) =>
    request<{ job: DocImportJob }>('/api/admin/doc-import', {
      method: 'POST',
      body: JSON.stringify({ md: { name: md.name, size: md.size }, pdf: { name: pdf.name, size: pdf.size } }),
    }),
  adminDocImportUpload: (jobId: string, kind: 'md' | 'pdf', offset: number, chunk: Blob) =>
    request<{ received: number }>(`/api/admin/doc-import/${jobId}/${kind}?offset=${offset}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: chunk,
    }),
  adminDocImportConvert: (jobId: string) =>
    request<{ job: DocImportJob }>(`/api/admin/doc-import/${jobId}/convert`, { method: 'POST' }),
  adminDocImportGet: (jobId: string) => request<{ job: DocImportJob }>(`/api/admin/doc-import/${jobId}`),
  adminDocImportCreate: (jobId: string, body: { title?: string; slug?: string; skip_title_page?: boolean }) =>
    request<{ workshop: AdminWorkshop }>(`/api/admin/doc-import/${jobId}/create`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminDocImportDiscard: (jobId: string) =>
    request<void>(`/api/admin/doc-import/${jobId}`, { method: 'DELETE' }),

  // ── Reassign workshop ownership (superadmin-only) ────────────
  adminReassignWorkshop: (workshopId: string, targetOwnerId: string) =>
    request<{ workshop: AdminWorkshop; unchanged?: boolean }>(
      `/api/admin/workshops/${workshopId}/reassign`,
      { method: 'POST', body: JSON.stringify({ target_owner_id: targetOwnerId }) },
    ),

  // ── Archived workshops — archive-on-delete (superadmin-only) ─
  adminListArchived: () =>
    request<{ archived: ArchivedWorkshopEntry[] }>('/api/admin/archived'),

  adminRestoreArchived: (file: string, targetOwnerId: string) =>
    request<{
      workshop: AdminWorkshop
      renamed: boolean
      requested_slug: string
      final_slug: string
    }>(`/api/admin/archived/${encodeURIComponent(file)}/restore`, {
      method: 'POST',
      body: JSON.stringify({ target_owner_id: targetOwnerId }),
    }),

  // ── Participant challenges ───────────────────────────────────
  participantListChallenges: (slug: string) =>
    request<{ challenges: PublicChallenge[] }>(`/api/workshops/${slug}/challenges`),

  participantSubmit: (
    slug: string,
    cid: string,
    answer: unknown,
  ) =>
    request<{
      is_correct: boolean
      points_awarded: number
      attempt_count: number
      already_solved: boolean
      base_points: number
      attempts_remaining: number | null
    }>(`/api/workshops/${slug}/challenges/${cid}/submit`, {
      method: 'POST',
      body: JSON.stringify({ answer }),
    }),

  participantRevealHint: (slug: string, cid: string) =>
    request<HintRevealResponse>(`/api/workshops/${slug}/challenges/${cid}/hints/reveal`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  participantSubmissions: (slug: string) =>
    request<{ submissions: ParticipantSubmission[] }>(`/api/workshops/${slug}/submissions`),

  participantLeaderboard: (slug: string) =>
    request<{ entries: LeaderboardEntry[]; first_blood_icon: string }>(
      `/api/workshops/${slug}/leaderboard`,
    ),

  participantEventFeed: (slug: string, since: number) =>
    request<{ events: FeedEvent[] }>(`/api/workshops/${slug}/event-feed?since=${since}`),

  participantChapters: (slug: string) =>
    request<{ chapters: ParticipantChapterMeta[] }>(`/api/workshops/${slug}/chapters`),

  participantChapter: (slug: string, chapterSlug: string) =>
    request<{ chapter: ChapterMeta; html: string }>(`/api/workshops/${slug}/chapters/${chapterSlug}`),

  // ── Participant downloads (cookie-auth GETs — used as <a href> targets) ──
  participantLabGuidePdfUrl: (slug: string) => `/api/workshops/${slug}/export/lab-guide`,
  participantCertificateUrl: (slug: string) => `/api/workshops/${slug}/export/certificate`,

  adminUploadImage: async (workshopId: string, file: File | Blob): Promise<{ id: string; url: string }> => {
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(`/api/admin/workshops/${workshopId}/images`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'upload_failed' }))
      throw new ApiError(res.status, (body as { error?: string }).error ?? 'upload_failed')
    }
    return (await res.json()) as { id: string; url: string }
  },

  adminListMedia: (workshopId: string) =>
    request<{ media: MediaItem[] }>(`/api/admin/workshops/${workshopId}/images`),

  adminDeleteMedia: (workshopId: string, mediaId: string) =>
    request<void>(`/api/admin/workshops/${workshopId}/images/${mediaId}`, { method: 'DELETE' }),

  adminListArchivedMedia: (workshopId: string) =>
    request<{ media: MediaItem[] }>(`/api/admin/workshops/${workshopId}/images/archive`),

  adminRestoreMedia: (workshopId: string, mediaId: string) =>
    request<void>(`/api/admin/workshops/${workshopId}/images/archive/${mediaId}/restore`, {
      method: 'POST',
    }),

  adminPurgeArchive: (workshopId: string) =>
    request<{ removed: number }>(`/api/admin/workshops/${workshopId}/images/archive`, {
      method: 'DELETE',
    }),

  // ── Clone + template library (Phase C) ──────────────────────────
  adminCloneWorkshop: (workshopId: string, body: { title?: string; slug?: string }) =>
    request<{ workshop: AdminWorkshop }>(
      `/api/admin/workshops/${workshopId}/clone`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  adminSaveAsTemplate: (
    workshopId: string,
    body: {
      title?: string
      description?: string
      note?: string
      visibility?: 'public' | 'private'
    },
  ) =>
    request<{ template: TemplateManifest }>(
      `/api/admin/workshops/${workshopId}/save-as-template`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  adminListTemplates: () =>
    request<{ templates: TemplateManifest[] }>('/api/admin/templates'),

  adminDeployTemplate: (
    templateId: string,
    body: { title?: string; slug?: string; auth_mode?: 'password' | 'magic_link' },
  ) =>
    request<{ workshop: AdminWorkshop }>(
      `/api/admin/templates/${templateId}/deploy`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** v0.13 templates-cleanup — toggle a template's visibility. Owner-only. */
  adminSetTemplateVisibility: (templateId: string, visibility: 'public' | 'private') =>
    request<{ template: TemplateManifest }>(
      `/api/admin/templates/${templateId}/visibility`,
      { method: 'PUT', body: JSON.stringify({ visibility }) },
    ),

  adminDeleteTemplate: (templateId: string) =>
    request<void>(`/api/admin/templates/${templateId}`, { method: 'DELETE' }),
}

export interface TemplateManifest {
  id: string
  title: string
  description: string
  /** Optional operator note — context the admin captures at save time. */
  note?: string
  created_at: string
  source_slug: string
  source_workshop_id: string
  /** v0.13 templates-cleanup — who can see the template + deploy it. */
  visibility?: 'public' | 'private'
  /** v0.13 templates-cleanup — creator of the template. Drives mutation gating. */
  owner_id?: string
  admin_slug?: string
  /** Server-stamped per request: true when the caller owns this template. */
  mine?: boolean
}

export interface MediaItem {
  id: string
  ext: string
  mimeType: string
  size_bytes: number
  created_at: string
  url: string
  used_in_chapters: Array<{ id: string; title: string }>
  used_in_challenges: Array<{ id: string; title: string }>
}

export type AuditEventType = 'submission' | 'hint_reveal' | 'admin_revert'

export interface AuditEvent {
  id: string
  ts: string
  event_type: AuditEventType
  user_id: string | null
  display_name: string | null
  challenge_id: string | null
  challenge_title: string | null
  chapter_id: string | null
  chapter_title: string | null
  actor: string | null
  payload: Record<string, unknown>
  reverted_by: string | null
}

export interface AdminWorkshop {
  id: string
  slug: string
  title: string
  description: string
  auth_mode: 'password' | 'magic_link'
  status: 'draft' | 'active' | 'closed'
  created_at: string
  closed_at: string | null
  // Timed-mode fields (optional — pre-feature workshops won't have them).
  timed_mode?: boolean
  duration_minutes?: number | null
  started_at?: string | null
  // Phase B — display mode (optional; absent = standard behavior).
  mode?: 'standard' | 'ctf'
  ctf_view?: 'normal' | 'jeopardy'
  /** Enter-submit on text answers (optional; absent ≡ true). */
  enter_submits_text_answers?: boolean
  /** v0.8 — First Blood bonus + icon. Both optional; absent ≡ 0 / 🩸. */
  first_blood_bonus?: number
  first_blood_icon?: string
  /** v0.10 — Speed-bump protection. Null/absent = unlocked. */
  protected?: WorkshopProtection | null
  /** v0.12 — Hidden from the public landing page. Absent ≡ false (listed). */
  unlisted?: boolean
  /** v0.13 multi-tenant. Catalog visibility + cross-admin scope. */
  publish_state?: 'hidden' | 'private' | 'public'
  /** v0.13 multi-tenant. Template gallery visibility. */
  /** v0.13 multi-tenant. Owning admin id. */
  owner_id?: string
  /** v0.13 multi-tenant. Owning admin slug (denormalized). */
  admin_slug?: string
  /**
   * v0.13 multi-tenant. Per-request access tag from the list endpoint.
   * 'owner' = full edit + delete; 'scope' = operate-only (workshop is
   * marked global and someone else owns it). Absent on detail endpoints.
   */
  _access?: 'owner' | 'scope'
}

export interface WorkshopProtection {
  by_admin_id: string
  by_admin_email: string
  at: string
}

/** One archived (deleted) workshop awaiting purge or restore. Superadmin-only. */
export interface ArchivedWorkshopEntry {
  file: string
  slug: string
  workshop_id: string | null
  title: string | null
  archived_at: string
  archived_by_email: string | null
  original_owner_id: string | null
  original_owner_email: string | null
  purge_at: string
  days_until_purge: number
  size_bytes: number
}

export interface AdminAuditEvent {
  id: string
  ts: string
  actor_id: string
  actor_email: string
  action: string
  target_type: string | null
  target_id: string | null
  target_label: string | null
  remote_ip: string | null
  user_agent: string | null
  outcome: 'success' | 'failure'
  error_code: string | null
  payload: Record<string, unknown>
}

export interface AdminAuditActor {
  actor_id: string
  actor_email: string
}

export interface BackupManifestContents {
  workshops: Array<{ slug: string; title: string }>
  admins_count: number
  templates_count: number
  has_audit_db: boolean
  has_certs: boolean
  has_branding: boolean
  has_env: boolean
}

export interface BackupManifest {
  schema_version: number
  platform_version: string
  created_at: string
  source_hostname: string | null
  contents: BackupManifestContents
}

export interface RestoreResult {
  status: 'restored'
  restarting: boolean
  manifest: BackupManifest
  preserved_data_dir: string
  preserved_env_file: string | null
  warnings: string[]
}

export interface ChallengesFilePlanItem {
  title: string
  chapter: string
  changes?: string[]
  solves?: number
  unknown_id?: string
}

export interface ChallengesFilePlan {
  problems: string[]
  created: ChallengesFilePlanItem[]
  updated: ChallengesFilePlanItem[]
  deleted: ChallengesFilePlanItem[]
  unchanged: number
  reordered: boolean
  live: { active: boolean; solves: number }
}

export interface ChapterMeta {
  id: string
  title: string
  slug: string
  position: number
  published: boolean
  /** Title page / welcome chapter: first in the menu, unnumbered. */
  title_page?: boolean
  updated_at: string
}

/** A chapter in the participant menu, with its Heading 2 sub-sections. */
export interface ParticipantChapterMeta extends ChapterMeta {
  sections: { id: string; title: string }[]
}

export interface ChapterFull extends ChapterMeta {
  content: string
}

export type ChallengeType = 'text_match' | 'multiple_choice' | 'checklist'

/** What admins see for each challenge type (the stored values stay as they are). */
export const CHALLENGE_TYPE_LABELS: Record<ChallengeType, string> = {
  text_match: 'Typed answer',
  multiple_choice: 'Multiple choice',
  checklist: 'Checklist',
}

export interface TextMatchAnswer {
  correct_answers: string[]
  case_sensitive: boolean
  use_regex: boolean
}

export interface MultipleChoiceOption {
  id: string
  text: string
}

export interface MultipleChoiceAnswer {
  options: MultipleChoiceOption[]
  correct_ids: string[]
  allow_multiple: boolean
}

export interface ChecklistItem {
  id: string
  label: string
}

export interface ChecklistAnswer {
  items: ChecklistItem[]
  require_all: boolean
}

export type AnswerData = TextMatchAnswer | MultipleChoiceAnswer | ChecklistAnswer

export interface Hint {
  text: string
  cost: number
}

export interface ChallengeDecay {
  enabled: boolean
  per_solve: number
  floor?: number
}

export interface ChallengeFile {
  id: string
  filename: string
  mime: string
  size: number
  created_at: string
}

export interface AdminChallenge {
  id: string
  title: string
  description: string
  type: ChallengeType
  points: number
  position: number
  chapter_id: string | null
  is_published: boolean
  max_attempts: number | null
  hints: Hint[]
  /** Phase A — challenge IDs that must be solved before this one is visible. */
  unlock_requires?: string[]
  /** Phase A — CTFd-style point decay. */
  decay?: ChallengeDecay
  /** v0.8 — per-challenge file attachments. */
  files?: ChallengeFile[]
  /**
   * Distinct-solver count snapshot at fetch time. Filled in by the admin
   * challenges list endpoint; absent on single-challenge reads and POST/PUT
   * responses where the count isn't joined in. Defaults to 0 in the UI when
   * undefined.
   */
  solves?: number
  answer_data: AnswerData
  created_at: string
}

export interface AdminChallengePayload {
  title: string
  description?: string
  type: ChallengeType
  points: number
  chapter_id?: string | null
  is_published?: boolean
  max_attempts?: number | null
  hints?: Hint[]
  unlock_requires?: string[]
  decay?: ChallengeDecay
  answer_data: AnswerData
}

// Participant-facing challenge — answer_data + hint texts are stripped. We
// expose hint count + per-hint cost so the UI can render the reveal button.
// `description_html` is the server-rendered + sanitized HTML form of the
// markdown `description`; the raw text stays available for legacy callers
// but the card prefers the HTML render.
type PublicChallengeBase = Omit<
  AdminChallenge,
  'answer_data' | 'hints' | 'solves' | 'files'
> & {
  description_html: string
  /** Distinct-solver count, always present on the participant payload. */
  solves: number
  /**
   * True once SOME participant has claimed first blood on this challenge.
   * The Jeopardy board renders the workshop's first_blood_icon as a corner
   * badge to communicate "first blood here is gone". Independent of whether
   * the current viewer was the one who got it.
   */
  first_blood_taken: boolean
  /** Per-challenge attachments — always an array (empty when none). */
  files: ChallengeFile[]
  hints_count: number
  hint_costs: number[]
}
export type PublicChallenge =
  | (PublicChallengeBase & { type: 'text_match' })
  | (PublicChallengeBase & {
      type: 'multiple_choice'
      options: MultipleChoiceOption[]
      allow_multiple: boolean
    })
  | (PublicChallengeBase & {
      type: 'checklist'
      items: ChecklistItem[]
      require_all: boolean
    })

export interface LeaderboardEntry {
  user_id: string
  display_name: string
  total_score: number
  challenges_solved: number
  /** Count of challenges this participant first-blooded. */
  first_bloods: number
  last_solve_at: string | null
  rank: number
}

/**
 * One entry in the recent-activity event feed (in-memory ring buffer on the
 * server, polled every few seconds by the projector + leaderboard pages).
 */
export interface FeedEvent {
  id: number
  ts: string
  kind: 'solve' | 'first_blood' | 'lockout' | 'hint'
  display_name: string
  challenge_title: string
  points?: number
}

export interface AdminParticipant {
  id: string
  email: string
  display_name: string
  role: 'participant' | 'admin'
  registered_at: string
  last_seen_at: string
  has_password: boolean
}

export type TlsMode = 'auto' | 'cloudflare' | 'http01' | 'byo' | 'internal'

export interface TlsMasked {
  mode: TlsMode
  hostname: string | null
  acme_email: string | null
  cloudflare_api_token: string | null
  has_cloudflare_api_token: boolean
  cert_file: string | null
  key_file: string | null
}

export type TlsStatus =
  | { ok: false; error: string }
  | {
      ok: true
      kind: 'lets_encrypt' | 'internal' | 'byo' | 'other'
      issuer: { cn: string | null; o: string | null }
      subject: { cn: string | null; alt_names: string | null }
      valid_from: string | null
      valid_to: string | null
      days_until_expiry: number | null
      protocol: string | null
    }

export interface TlsUpdateBody {
  mode?: TlsMode
  hostname?: string | null
  acme_email?: string | null
  cloudflare_api_token?: string | null
  clear_cloudflare_api_token?: boolean
}

export interface BrandingPublic {
  name: string | null
  tagline: string | null
  logo_url: string | null
  banner_url: string | null
  lobby_image_url: string | null
}

export interface BrandingResolved {
  name: string
  tagline: string | null
  logo_url: string | null
  banner_url: string | null
  lobby_image_url: string | null
  /** v0.9.0 — stored palette name, or null for "use emerald default". */
  accent_palette?: string | null
}

export interface BrandingUpdateBody {
  name?: string | null
  tagline?: string | null
  logo_url?: string | null
  banner_url?: string | null
  lobby_image_url?: string | null
  accent_palette?: string | null
}

export interface LobbyState {
  enabled: boolean
  /**
   * Plaintext entry code when set under v0.11.2+. `null` when only a
   * legacy bcrypt hash exists from a pre-v0.11.2 install — the UI then
   * nudges the admin to rotate so the code becomes viewable.
   */
  code: string | null
  code_set: boolean
  code_version: number
}

export type EmailMasked =
  | { kind: 'gmail'; user: string; app_password: string; has_app_password: boolean; from: string | null }
  | {
      kind: 'smtp'
      host: string
      port: number
      user: string | null
      pass: string | null
      has_pass: boolean
      from: string
    }
  | { kind: 'none' }

export interface EmailUpdateBody {
  kind: 'gmail' | 'smtp' | 'none'
  gmail?: { user?: string; app_password?: string; from?: string | null }
  smtp?: { host?: string; port?: number; user?: string | null; pass?: string | null; from?: string }
}

export interface PlatformSettingsResponse {
  branding: BrandingResolved
  tls: TlsMasked
  email: EmailMasked
  default_auth_mode: 'password' | 'magic_link'
  deploy_mode: 'direct' | 'tunnel'
  lobby: LobbyState
}

export type EditionName = 'community' | 'hosted' | 'commercial-self-hosted'

export interface EditionInfo {
  name: EditionName
  /** `null` when unlimited; integer when capped. Community = 1. */
  max_admins: number | null
  watermarks_enabled: boolean
  licensed_org: string | null
  license_id: string | null
  license_expires_at: string | null
  license_contact: string | null
  license_features: string[]
}

export interface CsrRequestBody {
  common_name: string
  alt_names: string[]
  organization: string
  org_unit?: string
  locality: string
  state: string
  country: string
  email?: string
  key_type: 'rsa2048' | 'ecdsa-p256'
}

export interface PendingCsr {
  subject: Omit<CsrRequestBody, 'key_type'>
  key_type: 'rsa2048' | 'ecdsa-p256'
  created_at: string
  csr_pem: string
}

export interface InstalledCert {
  subject: string
  issuer: string
  valid_from: string
  valid_to: string
  alt_names: string | null
  chain_length: number
}

export interface UpdatesSnapshot {
  current_version: string
  /** Root updater + releases/ layout present on this host. */
  updater_installed: boolean
  auto_check: boolean
  feed_url: string
  /** Browsable releases page for the feed (GitHub feeds only), for offline downloads. */
  releases_url: string | null
  last_checked_at: string | null
  last_error: string | null
  available: {
    version: string
    published_at: string
    notes_md: string
    action_required: string | null
    size: number
    /** Why it can't be applied automatically (e.g. `needs_node_22`), or null. */
    blocked_reason: string | null
  } | null
  status: { phase: string; message: string; version: string | null; at: string } | null
  /** Where `available` came from: the release feed or an uploaded offline package. */
  source: 'feed' | 'upload' | null
  /** Background download/stage of the requested release (this process's memory). */
  apply: { version: string; phase: 'downloading' | 'requested' | 'failed'; received: number; total: number; error: string | null } | null
  history: Array<{ result: string; from: string | null; to: string | null; by: string | null; message: string; at: string }>
  active_participants: number
}

export interface LicenseSnapshot {
  edition: EditionInfo
  /** Whether the licensee org name appears on public surfaces (v1.2.0). */
  show_licensed_org: boolean
  license_source: string | null
  license_issued_at: string | null
  describe: string
}

export interface LicenseRequestBody {
  contactName: string
  companyName: string
  contactEmail: string
  contactPhone: string
  address: string
  deployment: 'self-hosted' | 'hosted' | 'unsure'
  estimatedAdmins: number | null
  estimatedWorkshops: number | null
  notes: string
  /** Honeypot — leave empty. Bots fill it; we silently drop those. */
  website?: string
}

export interface AdminAccount {
  id: string
  email: string
  created_at: string
  /** v0.13 multi-tenant fields. May be null on pre-migration legacy data. */
  admin_slug: string | null
  display_name: string
  role: 'admin' | 'superadmin'
  status: 'active' | 'pending' | 'disabled'
  /** Pending only: when the invite was sent. */
  invited_at?: string | null
  /** Active/disabled: when they accepted the invite (or the account was created active). */
  accepted_at?: string | null
  /** How many workshops this admin owns. */
  workshops_owned?: number
}

/** What an admin owns, for the delete dialog. */
export interface AdminOwned {
  workshops: Array<{
    id: string
    slug: string
    title: string
    status: 'draft' | 'active' | 'closed'
    participants: number
    protected: boolean
  }>
  templates: Array<{ id: string; title: string; visibility: string }>
}

/** v0.13 — signup request row from the queue. */
export interface AdminSignupRequest {
  id: string
  email: string
  requested_slug: string
  display_name: string
  reason: string
  submitted_at: string
  status: 'pending' | 'approved' | 'rejected'
  decided_at: string | null
  decided_by: string | null
}

export interface ParticipantSubmission {
  id: string
  challenge_id: string
  user_id: string
  submitted_answer: string
  is_correct: boolean
  points_awarded: number
  attempt_count: number
  hints_used: number
  submitted_at: string
}

export interface HintRevealResponse {
  hint: { index: number; text: string; cost: number }
  hints_used: number
  hints_remaining: number
}

export interface DocImportProblem {
  kind: string
  detail: string
  image?: number
  section?: string
  page?: number
}

export interface DocImportReport {
  ok: boolean
  title: string
  md_images: number
  pdf_images: number
  pdf_decorations: number
  max_pixel_diff: number | null
  chapters: {
    title: string
    /** The heading as written, when section numbering was stripped. */
    source_title?: string
    images: number
    challenges: number
    skipped: boolean
    title_page?: boolean
  }[]
  problems: DocImportProblem[]
  problem_count: number
}

export interface DocImportJob {
  id: string
  state: 'uploading' | 'converting' | 'ready' | 'rejected' | 'failed'
  files: Record<'md' | 'pdf', { name: string; size: number; received: number }>
  progress: { phase: 'reading_pdf' | 'checking_images' | 'building'; done: number; total: number } | null
  report: DocImportReport | null
  error: string | null
  created_at: string
}
